export const DATABASE_ATTACHMENT_CELL_FILES_EVENT =
  'database-attachment-cell-files';

export interface DatabaseAttachmentBlobReference {
  blobId: string;
  name: string;
  mime: string;
  size: number | null;
}

export type DatabaseAttachmentCellFilesEvent = CustomEvent<{
  files?: File[];
  blobs?: DatabaseAttachmentBlobReference[];
}>;

const SELECTED_ATTACHMENT_CELL_SELECTOR =
  'dv-table-view-cell-container[data-database-cell-selected="true"][data-column-type="attachment"], ' +
  'affine-database-virtual-cell-container[data-database-cell-selected="true"][data-column-type="attachment"]';

let lastSelectedAttachmentCell: HTMLElement | null = null;

export function getClipboardFiles(clipboardData: DataTransfer): File[] {
  const files = Array.from(clipboardData.files);
  if (files.length > 0) {
    return files;
  }

  return Array.from(clipboardData.items)
    .filter(item => item.kind === 'file')
    .map(item => item.getAsFile())
    .filter((file): file is File => !!file);
}

function getVisibleRect(element: HTMLElement): DOMRect {
  const rect = element.getBoundingClientRect();
  if (rect.width > 0 && rect.height > 0) {
    return rect;
  }

  const frame = element.closest<HTMLElement>('[data-database-cell-frame]');
  return frame?.getBoundingClientRect() ?? rect;
}

function isVisibleElement(element: HTMLElement): boolean {
  const rect = getVisibleRect(element);
  return rect.width > 0 && rect.height > 0;
}

function getSelectedAttachmentCellElement(): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const activeCell = document.activeElement?.closest<HTMLElement>(
    SELECTED_ATTACHMENT_CELL_SELECTOR
  );
  if (activeCell && isVisibleElement(activeCell)) {
    return activeCell;
  }

  const cells = Array.from(
    document.querySelectorAll<HTMLElement>(SELECTED_ATTACHMENT_CELL_SELECTOR)
  ).filter(isVisibleElement);
  return cells.at(-1) ?? null;
}

export function trackSelectedAttachmentCell(
  cell: HTMLElement,
  options: {
    columnType: string;
    selected: boolean;
  }
): void {
  if (options.selected && options.columnType === 'attachment') {
    lastSelectedAttachmentCell = cell;
    return;
  }

  if (lastSelectedAttachmentCell === cell) {
    lastSelectedAttachmentCell = null;
  }
}

function dispatchAttachmentPayloadToCell(
  cell: HTMLElement,
  detail: DatabaseAttachmentCellFilesEvent['detail']
): boolean {
  cell.dispatchEvent(
    new CustomEvent('database-paste-files', {
      bubbles: false,
      detail,
    })
  );
  return true;
}

function offerAttachmentPayloadToSelectedCell(
  detail: DatabaseAttachmentCellFilesEvent['detail']
): boolean {
  const hasPayload =
    (detail.files?.length ?? 0) > 0 || (detail.blobs?.length ?? 0) > 0;
  if (!hasPayload || typeof window === 'undefined') {
    return false;
  }

  if (
    lastSelectedAttachmentCell?.isConnected &&
    lastSelectedAttachmentCell.dataset['databaseCellSelected'] === 'true' &&
    lastSelectedAttachmentCell.dataset['columnType'] === 'attachment' &&
    isVisibleElement(lastSelectedAttachmentCell)
  ) {
    return dispatchAttachmentPayloadToCell(lastSelectedAttachmentCell, detail);
  }

  const selectedCell = getSelectedAttachmentCellElement();
  if (selectedCell) {
    return dispatchAttachmentPayloadToCell(selectedCell, detail);
  }

  const event = new CustomEvent(DATABASE_ATTACHMENT_CELL_FILES_EVENT, {
    cancelable: true,
    detail,
  });
  return !window.dispatchEvent(event);
}

export function offerFilesToSelectedAttachmentCell(files: File[]): boolean {
  return offerAttachmentPayloadToSelectedCell({ files });
}

export function offerBlobReferencesToSelectedAttachmentCell(
  blobs: DatabaseAttachmentBlobReference[]
): boolean {
  return offerAttachmentPayloadToSelectedCell({ blobs });
}
